import { useEffect } from "react";

/**
 * Lightweight per-route <head> updater. Sets <title>, description, canonical
 * and OG/Twitter meta tags via document. Works for modern JS-executing
 * crawlers (LinkedIn, Twitter/X, WhatsApp, Slack, Google). On restore, the
 * previous values are put back so we don't leak page-specific meta across
 * routes.
 */
export default function SEO({
  title,
  description,
  image,
  url,
  type = "website",
}) {
  useEffect(() => {
    const prev = {
      title: document.title,
      description: getMeta("name", "description"),
      ogTitle: getMeta("property", "og:title"),
      ogDescription: getMeta("property", "og:description"),
      ogImage: getMeta("property", "og:image"),
      ogImageSecure: getMeta("property", "og:image:secure_url"),
      ogUrl: getMeta("property", "og:url"),
      ogType: getMeta("property", "og:type"),
      twitterTitle: getMeta("name", "twitter:title"),
      twitterDescription: getMeta("name", "twitter:description"),
      twitterImage: getMeta("name", "twitter:image"),
      canonical: getLink("canonical"),
    };

    if (title) document.title = title;
    if (description) setMeta("name", "description", description);
    if (title) {
      setMeta("property", "og:title", title);
      setMeta("name", "twitter:title", title);
    }
    if (description) {
      setMeta("property", "og:description", description);
      setMeta("name", "twitter:description", description);
    }
    if (image) {
      setMeta("property", "og:image", image);
      setMeta("property", "og:image:secure_url", image);
      setMeta("name", "twitter:image", image);
    }
    if (type) setMeta("property", "og:type", type);
    if (url) {
      setMeta("property", "og:url", url);
      setLink("canonical", url);
    }

    return () => {
      // Restore previous values so other routes are not affected.
      if (prev.title) document.title = prev.title;
      if (prev.description != null) setMeta("name", "description", prev.description);
      if (prev.ogTitle != null) setMeta("property", "og:title", prev.ogTitle);
      if (prev.ogDescription != null) setMeta("property", "og:description", prev.ogDescription);
      if (prev.ogImage != null) setMeta("property", "og:image", prev.ogImage);
      if (prev.ogImageSecure != null) setMeta("property", "og:image:secure_url", prev.ogImageSecure);
      if (prev.ogUrl != null) setMeta("property", "og:url", prev.ogUrl);
      if (prev.ogType != null) setMeta("property", "og:type", prev.ogType);
      if (prev.twitterTitle != null) setMeta("name", "twitter:title", prev.twitterTitle);
      if (prev.twitterDescription != null) setMeta("name", "twitter:description", prev.twitterDescription);
      if (prev.twitterImage != null) setMeta("name", "twitter:image", prev.twitterImage);
      if (prev.canonical) setLink("canonical", prev.canonical);
      else removeLink("canonical");
    };
  }, [title, description, image, url, type]);

  return null;
}

function getMeta(attr, key) {
  const el = document.head.querySelector(`meta[${attr}="${key}"]`);
  return el ? el.getAttribute("content") : null;
}

function setMeta(attr, key, value) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function getLink(rel) {
  const el = document.head.querySelector(`link[rel="${rel}"]`);
  return el ? el.getAttribute("href") : null;
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function removeLink(rel) {
  const el = document.head.querySelector(`link[rel="${rel}"]`);
  if (el) el.remove();
}
